import React, { useContext, useMemo, useState } from 'react';

import { Alert } from 'antd';
import { Formik, Field } from 'formik';
import * as Yup from 'yup';

import { getCustomFieldInitialValue, getEmbeddedCustomField } from '@libs/NwCustomFields';
import { toISODate } from '@libs/NwMoment';

import { ListContext } from '@contexts/ListContext';

import { useUpdateProject, useUpdateProjectCustomFields } from '@hooks/API/Project/UseProject';
import useFormFieldFocus from '@hooks/Utils/UseFormFieldFocus';

import { NwCancelButton, NwSaveButton } from '@components/Gui/NwButton';
import NwDrawer from '@components/Gui/NwDrawer';
import { NWTextAreaCharCounter, NWInput, NWSelect } from '@components/Gui/NwForm/NwFormItems';
import { NwForm, NwFormButtonsBar } from '@components/Gui/NwForm/NwFormWrapper';

const ProjectEditDrawer = ({ project, customFields, onClose }) => {
    useFormFieldFocus('Name');
    //const { data: project } = useProject(projectId);
    const { mutateAsync: onUpdateProject } = useUpdateProject();
    const { mutateAsync: onUpdateProjectCustomFields } = useUpdateProjectCustomFields();
    const { cachedList } = useContext(ListContext);
    const [touchForm, setTouchForm] = useState(false);
    const [errorSaving, setErrorSaving] = useState(null);

    let initialValues = {
        Name: project.Name,
        Description: project.Description,
        Product: project.Product,
        ProjectCategoryID: project.ProjectCategoryID,
        PONumber: project.PONumber,
        AccountingNote: project.AccountingNote,
        CustomFields: {},
    };

    const availableCustomFields = customFields ? customFields.filter((field) => !field.Disabled) : [];

    availableCustomFields.map((customField) => {
        if (!customField.Disabled) {
            initialValues = {
                ...initialValues,
                CustomFields: {
                    ...initialValues.CustomFields,
                    [customField.Name]: getCustomFieldInitialValue(
                        customField.TypeName,
                        project.CustomFields[customField.Name]
                    ),
                },
            };
        }

        return null;
    });

    const saveProject = async (values, setSubmitting) => {
        const isCustomFieldsTouched =
            JSON.stringify(initialValues.CustomFields) !== JSON.stringify(values.CustomFields);

        const somethingChanged =
            JSON.stringify({ ...initialValues, CustomFields: {} }) !== JSON.stringify({ ...values, CustomFields: {} });

        if (somethingChanged) {
            const projectData = [
                { name: 'Name', value: values.Name, changed: true, errormessage: '', updated: true },
                { name: 'Product', value: values.Product, changed: true, errormessage: '', updated: true },
                { name: 'Description', value: values.Description, changed: true, errormessage: '', updated: true },
                { name: 'PONumber', value: values.PONumber, changed: true, errormessage: '', updated: true },
                {
                    name: 'AccountingNote',
                    value: values.AccountingNote,
                    changed: true,
                    errormessage: '',
                    updated: true,
                },
                {
                    name: 'ProjectCategoryID',
                    value: values.ProjectCategoryID,
                    changed: true,
                    errormessage: '',
                    updated: true,
                },
            ];

            try {
                await onUpdateProject({ projectId: project.ID, data: projectData });
                if (!isCustomFieldsTouched) {
                    setTouchForm(false);
                    onClose();
                }
            } catch (error) {
                setErrorSaving('There was an error saving the project. Please try again.');
                setSubmitting(false);
            }
        }

        if (isCustomFieldsTouched) {
            let customFieldsData = {};
            Object.keys(values.CustomFields).map((key) => {
                if (
                    values.CustomFields[key] !== '' &&
                    JSON.stringify(values.CustomFields[key]) !== JSON.stringify([]) &&
                    values.CustomFields[key]
                ) {
                    const customField = availableCustomFields.find((item) => item.Name === key);
                    if (customField.TypeName === 'MultipleChoice') {
                        customFieldsData = {
                            ...customFieldsData,
                            [key]: values.CustomFields[key].join(','),
                        };
                    } else if (customField.TypeName === 'Date') {
                        customFieldsData = {
                            ...customFieldsData,
                            [key]: toISODate(values.CustomFields[key]),
                        };
                    } else {
                        customFieldsData = {
                            ...customFieldsData,
                            [key]: values.CustomFields[key],
                        };
                    }
                }
                return null;
            });

            try {
                await onUpdateProjectCustomFields({ projectId: project.ID, data: customFieldsData });
                setTouchForm(false);
                onClose();
            } catch (error) {
                setErrorSaving('There was an error saving the project. Please try again.');
            }
        }
        if (!somethingChanged && !isCustomFieldsTouched) {
            setErrorSaving('No changes to save.');
            setTouchForm(false);
            setSubmitting(false);
        }
    };

    const categories = useMemo(() => {
        let categories = [...cachedList.projectCategories.items];
        categories.sort((cat1, cat2) => (cat1.Name.toLowerCase() > cat2.Name.toLowerCase() ? 1 : -1));
        return categories;
    }, [cachedList.projectCategories.items]);

    return (
        <NwDrawer touchForm={touchForm} onTouchForm={setTouchForm} title={'Edit Project'} onClose={onClose}>
            <Formik
                initialValues={initialValues}
                onSubmit={(values, { setSubmitting }) => {
                    saveProject(values, setSubmitting);
                }}
                validationSchema={Yup.object().shape({
                    Name: Yup.string().required('Content is required'),
                })}
            >
                {({ isSubmitting, values, handleSubmit }) => (
                    <NwForm values={values} onTouchForm={setTouchForm} onFinish={handleSubmit} layout="vertical">
                        <Field
                            component={NWInput}
                            label="Title"
                            name="Name"
                            type="text"
                            id="Name"
                            value={values.Name}
                            required
                            maxLength={255}
                        />
                        <Field
                            component={NWInput}
                            label="Product"
                            name="Product"
                            type="text"
                            id="Product"
                            value={values.Product}
                            maxLength={255}
                        />
                        <Field
                            component={NWSelect}
                            label="Category"
                            name="ProjectCategoryID"
                            placeholder="Select a category"
                            value={values.ProjectCategoryID}
                            getPopupContainer={(triggerNode) => triggerNode.parentNode}
                            options={categories.map((category) => {
                                return { key: category.ID, label: category.Name, value: category.ID };
                            })}
                        />
                        <Field
                            component={NWTextAreaCharCounter}
                            label="Description"
                            name="Description"
                            type="text"
                            value={values.Description}
                            maxLength={999}
                            autoSize={{ minRows: 5, maxRows: 10 }}
                        />
                        <Field
                            component={NWInput}
                            label="P.O. Number"
                            name="PONumber"
                            type="text"
                            value={values.PONumber}
                        />
                        <Field
                            component={NWTextAreaCharCounter}
                            label="Accounting Note"
                            name="AccountingNote"
                            type="text"
                            value={values.AccountingNote}
                        />
                        {availableCustomFields.map((customField, index) => {
                            return (
                                <div key={`${customField.Name}-${index}`}>
                                    {getEmbeddedCustomField(values, customField)}
                                </div>
                            );
                        })}
                        {errorSaving && (
                            <Alert
                                style={{ marginTop: '1rem' }}
                                message="Error"
                                description={errorSaving}
                                type="error"
                                showIcon
                            />
                        )}
                        <br />
                        <NwFormButtonsBar
                            left={
                                <NwCancelButton
                                    disabled={isSubmitting}
                                    onClick={() => {
                                        setTouchForm(false);
                                        onClose();
                                    }}
                                />
                            }
                            right={<NwSaveButton htmlType="submit" disabled={isSubmitting} loading={isSubmitting} />}
                        />
                    </NwForm>
                )}
            </Formik>
        </NwDrawer>
    );
};

export default ProjectEditDrawer;
